import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import { canManageCrmTickets } from '../../../utils/crmPermissions';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card, CardContent } from '../../ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { isMobileContainer } from '../../../utils/device';
import { CRM_TICKET_CATEGORIES, CRM_TICKET_PRIORITIES, ticketCategoryLabel } from '../../../data/crmTicketConstants';
import CrmTicketStatusBadge from './CrmTicketStatusBadge';
import CrmTicketPriorityBadge from './CrmTicketPriorityBadge';
import CrmTicketSlaBadge from './CrmTicketSlaBadge';
import { Download, Search } from 'lucide-react';
import { useToast } from '../../../hooks/use-toast';
import { exportTicketCsv } from '../../../utils/crmTicketDownload';

export default function CrmTicketList({
  statuses = [],
  refreshKey = 0,
  relatedEmployeeId = '',
  listFilters = { status: 'all', overdue: false },
}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], total: 0, page: 1, total_pages: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    status: listFilters.status || 'all',
    priority: 'all',
    category: 'all',
    overdue: !!listFilters.overdue,
    sort: 'updated_at',
    order: 'desc',
    page: 1,
  });

  useEffect(() => {
    setFilters((f) => ({
      ...f,
      status: listFilters.status || 'all',
      overdue: !!listFilters.overdue,
      page: 1,
    }));
  }, [listFilters.status, listFilters.overdue]);

  const load = () => {
    setLoading(true);
    const params = { page: filters.page, limit: 20, sort: filters.sort, order: filters.order };
    if (filters.search) params.search = filters.search;
    if (filters.status !== 'all') params.status = filters.status;
    if (filters.priority !== 'all') params.priority = filters.priority;
    if (filters.category !== 'all') params.category = filters.category;
    if (filters.overdue) params.overdue = 1;
    if (relatedEmployeeId) params.related_employee_id = relatedEmployeeId;
    api.get('/crm/tickets', { params })
      .then((r) => setData(r.data?.data || { items: [], total: 0, page: 1, total_pages: 0 }))
      .catch(() => setData({ items: [], total: 0, page: 1, total_pages: 0 }))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filters, refreshKey, relatedEmployeeId]);

  const exportCsv = async () => {
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.status !== 'all') params.status = filters.status;
      if (filters.priority !== 'all') params.priority = filters.priority;
      if (filters.category !== 'all') params.category = filters.category;
      if (filters.overdue) params.overdue = 1;
      if (relatedEmployeeId) params.related_employee_id = relatedEmployeeId;
      await exportTicketCsv(params);
    } catch (err) {
      toast({ title: 'Export failed', description: err.message, variant: 'destructive' });
    }
  };

  const updateFilters = (patch) => {
    setFilters((f) => ({
      ...f,
      ...patch,
      page: patch.page ?? 1,
      // Manual filter changes clear overdue unless explicitly kept
      overdue: Object.prototype.hasOwnProperty.call(patch, 'overdue') ? patch.overdue : false,
    }));
  };

  const mobile = isMobileContainer();

  return (
    <div className="space-y-4">
      {filters.overdue && (
        <p className="text-sm text-muted-foreground">Showing overdue tickets</p>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search tickets…"
            value={filters.search}
            onChange={(e) => updateFilters({ search: e.target.value })}
          />
        </div>
        <Select value={filters.status} onValueChange={(v) => updateFilters({ status: v })}>
          <SelectTrigger className="w-full sm:w-40"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.priority} onValueChange={(v) => updateFilters({ priority: v })}>
          <SelectTrigger className="w-full sm:w-36"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {CRM_TICKET_PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        {canManageCrmTickets(user) && (
          <Button variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-2" />Export</Button>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : mobile ? (
        <div className="space-y-3">
          {data.items.map((t) => (
            <Card
              key={t.id}
              className="border-border cursor-pointer"
              onClick={() => navigate(`/crm/tickets/${t.id}`)}
            >
              <CardContent className="p-4 space-y-2">
                <Link to={`/crm/tickets/${t.id}`} className="font-medium text-primary hover:underline">{t.ticket_code}</Link>
                <p className="text-sm">{t.title}</p>
                <div className="flex flex-wrap gap-2">
                  <CrmTicketStatusBadge status={t.status} statuses={statuses} />
                  <CrmTicketPriorityBadge priority={t.priority} />
                  <CrmTicketSlaBadge slaStatus={t.sla_status} remainingSeconds={t.remaining_seconds} dueDate={t.due_date} />
                </div>
                <p className="text-xs text-muted-foreground">{t.assigned_to_name || 'Unassigned'} · {ticketCategoryLabel(t.category)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3">ID</th>
                <th className="text-left p-3">Title</th>
                <th className="text-left p-3">School</th>
                <th className="text-left p-3">Category</th>
                <th className="text-left p-3">Priority</th>
                <th className="text-left p-3">Status</th>
                <th className="text-left p-3">Assigned</th>
                <th className="text-left p-3">Due</th>
                <th className="text-left p-3">Updated</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((t) => (
                <tr
                  key={t.id}
                  className="border-t hover:bg-muted/30 cursor-pointer"
                  onClick={() => navigate(`/crm/tickets/${t.id}`)}
                >
                  <td className="p-3"><Link to={`/crm/tickets/${t.id}`} className="text-primary hover:underline">{t.ticket_code}</Link></td>
                  <td className="p-3 max-w-xs truncate">{t.title}</td>
                  <td className="p-3">{t.school_name || '—'}</td>
                  <td className="p-3">{ticketCategoryLabel(t.category)}</td>
                  <td className="p-3"><CrmTicketPriorityBadge priority={t.priority} /></td>
                  <td className="p-3"><CrmTicketStatusBadge status={t.status} statuses={statuses} /></td>
                  <td className="p-3">{t.assigned_to_name || '—'}</td>
                  <td className="p-3">{t.due_date ? t.due_date.slice(0, 10) : '—'}</td>
                  <td className="p-3 text-muted-foreground">{t.updated_at?.slice(0, 16)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data.total_pages > 1 && (
        <div className="flex justify-center gap-2">
          <Button variant="outline" size="sm" disabled={filters.page <= 1} onClick={() => setFilters((f) => ({ ...f, page: f.page - 1 }))}>Previous</Button>
          <span className="text-sm self-center">Page {data.page} of {data.total_pages}</span>
          <Button variant="outline" size="sm" disabled={filters.page >= data.total_pages} onClick={() => setFilters((f) => ({ ...f, page: f.page + 1 }))}>Next</Button>
        </div>
      )}
    </div>
  );
}
